import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	Output,
	EventEmitter,
	forwardRef,
	ViewChild,
	ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgxEditorModule, Toolbar } from 'ngx-editor';
import { Editor, schema } from 'ngx-editor';
import { Plugin } from 'prosemirror-state';
import { DOMSerializer, Fragment } from 'prosemirror-model';

interface EditorContent {
	type: string;
	content: any[];
}

@Component({
	selector: 'rich-text-editor',
	standalone: true,
	imports: [CommonModule, NgxEditorModule, FormsModule],
	templateUrl: './rich-text-editor.component.html',
	styleUrls: ['./rich-text-editor.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RichTextEditorComponent),
			multi: true,
		},
	],
})
export class RichTextEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
	private isHtmlContentSet = false;
	private isInitialized = false;
	private _htmlContent: string = '';

	@Output() blur = new EventEmitter<void>();
	@ViewChild('editorContainer') editorContainer!: ElementRef;

	editor!: Editor;
	toolbar: Toolbar = [
		['bold', 'italic', 'underline', 'bullet_list', 'align_left', 'align_center', 'align_right'],
	];

	// Function to call when the value changes.
	onChange = (_: any) => {};
	// Function to call when the input is touched
	onTouched = () => {};

	// Get and Set value
	get htmlContent(): string {
		return this._htmlContent;
	}

	set htmlContent(newValue: string | EditorContent) {
		// Prevent ngModelChange from being fired the first time the value is set
		if (!this.isHtmlContentSet) {
			this.isHtmlContentSet = true;
			return;
		}
		//Convert the data to a raw HTML string if it is returned as JSON
		if (newValue && typeof newValue === 'object' && 'content' in newValue) {
			// Handle ProseMirror JSON content
			const serializer = DOMSerializer.fromSchema(schema);
			// Create a Fragment from the content array
			const fragment = Fragment.from(newValue.content.map(node => schema.nodeFromJSON(node)));
			const domFragment = serializer.serializeFragment(fragment);
			const div = document.createElement('div');
			div.appendChild(domFragment);
			this._htmlContent = div.innerHTML;
		} else {
			// Handle string content
			this._htmlContent = newValue as string;
		}

		this.onChange(this._htmlContent);
		this.onTouched();
	}

	ngOnInit(): void {
		const enterKeyPlugin = new Plugin({
			props: {
				handleKeyDown: (view, event) => {
					if (event.key === 'Enter') {
						const { state, dispatch } = view;
						dispatch(state.tr.insertText('\n'));
						return true;
					}
					return false;
				},
			},
		});

		this.editor = new Editor({
			schema,
			content: this.htmlContent,
			plugins: [enterKeyPlugin],
		});

		this.editor.valueChanges.subscribe(() => {
			setTimeout(() => {
				this.scrollToBottom();
				this.isInitialized = true;
			}, 0);
		});
	}

	private scrollToBottom(): void {
		if (this.isInitialized) {
			const editorElement = this.editorContainer?.nativeElement?.querySelector('.NgxEditor');
			this.editorContainer.nativeElement.scrollIntoView({
				block: 'nearest',
				behavior: 'smooth',
			});
			if (editorElement) {
				const isScrolledToBottom =
					editorElement.scrollHeight - editorElement.scrollTop <=
					editorElement.clientHeight + 50;

				if (isScrolledToBottom) {
					editorElement.scrollTop = editorElement.scrollHeight;
				}
			}
		}
	}

	// make sure to destory the editor
	ngOnDestroy(): void {
		this.editor.destroy();
	}

	onInputChange(newValue: string | EditorContent) {
		this.htmlContent = newValue;
	}

	onPaste(e: any) {
		setTimeout(() => {
			this.onChange(this.htmlContent);
		});
	}

	onBlur() {
		this.onTouched();
		this.blur.emit();
	}

	// Implemented as part of ControlValueAccessor
	writeValue(value: string): void {
		if (value !== undefined) {
			this._htmlContent = value;
		}
	}

	// Implemented as part of ControlValueAccessor
	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	// Implemented as part of ControlValueAccessor
	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	handleEnterKey(event: KeyboardEvent) {
		event.preventDefault();
		this.editor.commands.insertText('\n');
	}
}
